import { BasePaymentOption } from '@/types/pricing';
import config from '@/config';

export interface CreatePaymentOptionRequest {
  programId: string;
  programType: string;
  paymentOptions: BasePaymentOption[];
}

export interface CreatePaymentOptionResponse {
  success: boolean;
  data?: {
    paymentOptions: BasePaymentOption[];
  };
  error?: string;
  code?: string;
}

export interface UpdatePaymentOptionsRequest {
  programId: string;
  programType: string;
  paymentOptions: BasePaymentOption[];
}

export interface UpdatePaymentOptionsResponse {
  success: boolean;
  data?: {
    paymentOptions: BasePaymentOption[];
  };
  error?: string;
  code?: string;
}

/**
 * Payment Options API Service
 * Handles CRUD operations for payment options with Square integration
 * Updated to use correct backend endpoints based on backend analysis
 * Uses HTTP-only cookies for authentication (no Bearer tokens needed)
 */
export class PaymentOptionsAPI {
  private static baseURL = config.apiUrl;
  
  // Debug: Log the base URL being used
  static {
    console.log('PaymentOptionsAPI: Using baseURL:', config.apiUrl);
    console.log('PaymentOptionsAPI: Environment VITE_API_URL:', import.meta.env.VITE_API_URL);
  }

  /**
   * Check if user appears to be authenticated
   * Authentication is handled via HTTP-only cookies - no localStorage check needed
   */
  private static isAuthenticated(): boolean {
    // Auth is handled server-side via cookies
    // This method is kept for backward compatibility but always returns true
    // The API will handle auth validation
    return true;
  }

  /**
   * Debug utility to check authentication status
   */
  static debugAuthStatus(): void {
    console.log('🔐 PaymentOptionsAPI Authentication Debug:', {
      isAuthenticated: true,
      baseURL: this.baseURL,
      note: 'Backend uses HTTP-only cookies for authentication - no Bearer tokens needed',
    });
  }

  /**
   * Create new payment options for a program
   * Creates Square plans and stores payment options
   * Uses the correct backend endpoint: /api/subscription/plan
   */
  static async createPaymentOptions(
    request: CreatePaymentOptionRequest
  ): Promise<CreatePaymentOptionResponse> {
    try {
      // Check if user appears to be authenticated
      if (!this.isAuthenticated()) {
        console.error('PaymentOptionsAPI: User not authenticated');
        return {
          success: false,
          error: 'User not authenticated. Please log in again.',
          code: 'AUTH_ERROR',
        };
      }

      // For new programs, we need to create individual payment plans
      // The backend expects each payment option to be created as a separate plan
      const createdPlans = [];
      const errors = [];

      for (const paymentOption of request.paymentOptions) {
        try {
          // Map frontend plan type to backend plan type
          const planType = this.mapPlanTypeToBackend(paymentOption.planType);
          
          // Create the plan using the correct backend endpoint
          const fullUrl = `${this.baseURL}/api/subscription/plan`;
          console.log('PaymentOptionsAPI: Making request to:', fullUrl);
          console.log('PaymentOptionsAPI: Request payload:', {
            displayName: paymentOption.displayName,
            amount: paymentOption.amount,
            planType: planType,
            numberOfPayments: paymentOption.numberOfPayments,
          });
          
          const planResponse = await fetch(fullUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // Note: Backend uses HTTP-only cookies for authentication
              // No Authorization header needed - cookies are sent automatically
            },
            credentials: 'include', // Include cookies for authentication
            body: JSON.stringify({
              displayName: paymentOption.displayName,
              paymentOptionName:
                paymentOption.paymentOptionName?.trim() ||
                paymentOption.displayName,
              amount: paymentOption.amount,
              planType: planType,
              numberOfPayments: paymentOption.numberOfPayments,
              ...(request.programId && { programId: request.programId }),
              ...(request.programType && { programDeliveryMode: request.programType }),
            }),
          });
          
          console.log('PaymentOptionsAPI: Response status:', planResponse.status);
          console.log('PaymentOptionsAPI: Response headers:', Object.fromEntries(planResponse.headers.entries()));

          const planResult = await planResponse.json();

          if (!planResponse.ok) {
            console.error('PaymentOptionsAPI: Plan creation failed:', {
              status: planResponse.status,
              statusText: planResponse.statusText,
              response: planResult,
              request: {
                displayName: paymentOption.displayName,
                amount: paymentOption.amount,
                planType: planType,
                numberOfPayments: paymentOption.numberOfPayments,
              }
            });
            errors.push(`Failed to create plan "${paymentOption.displayName}": ${planResult.error || planResult.message || `HTTP ${planResponse.status}`}`);
            continue;
          }

          // Add the created plan to our results
          createdPlans.push({
            ...paymentOption,
            squarePlanId: planResult.plan?.id,
            slug: planResult.plan?.slug,
            // Update the ID to use the backend-generated ID
            id: planResult.plan?.id || paymentOption.id,
          });

        } catch (error) {
          console.error(`Error creating plan for ${paymentOption.displayName}:`, error);
          errors.push(`Failed to create plan "${paymentOption.displayName}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      if (errors.length > 0) {
        return {
          success: false,
          error: `Some payment options failed to create: ${errors.join('; ')}`,
          code: 'PARTIAL_FAILURE',
        };
      }

      return {
        success: true,
        data: { paymentOptions: createdPlans },
      };

    } catch (error) {
      console.error('Error creating payment options:', error);
      return {
        success: false,
        error: 'Network error occurred while creating payment options',
        code: 'NETWORK_ERROR',
      };
    }
  }

  /**
   * Update existing payment options for a program
   * Updates Square plans and payment options
   * Uses the correct backend endpoint: /api/subscription/plan/:planId
   */
  static async updatePaymentOptions(
    request: UpdatePaymentOptionsRequest
  ): Promise<UpdatePaymentOptionsResponse> {
    try {
      // Check if user appears to be authenticated
      if (!this.isAuthenticated()) {
        console.error('PaymentOptionsAPI: User not authenticated');
        return {
          success: false,
          error: 'User not authenticated. Please log in again.',
          code: 'AUTH_ERROR',
        };
      }

      const updatedPlans = [];
      const errors = [];

      for (const paymentOption of request.paymentOptions) {
        try {
          // Map frontend plan type to backend plan type
          const planType = this.mapPlanTypeToBackend(paymentOption.planType);
          
          // Update the plan using the correct backend endpoint
          const planResponse = await fetch(`${this.baseURL}/api/subscription/plan/${paymentOption.squarePlanId || paymentOption.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              // Note: Backend uses HTTP-only cookies for authentication
              // No Authorization header needed - cookies are sent automatically
            },
            credentials: 'include', // Include cookies for authentication
            body: JSON.stringify({
              displayName: paymentOption.displayName,
              amount: paymentOption.amount,
              planType: planType,
              numberOfPayments: paymentOption.numberOfPayments,
            }),
          });

          const planResult = await planResponse.json();

          if (!planResponse.ok) {
            console.error('PaymentOptionsAPI: Plan update failed:', {
              status: planResponse.status,
              statusText: planResponse.statusText,
              response: planResult,
              planId: paymentOption.squarePlanId || paymentOption.id,
            });
            errors.push(`Failed to update plan "${paymentOption.displayName}": ${planResult.error || planResult.message || `HTTP ${planResponse.status}`}`);
            continue;
          }

          // Add the updated plan to our results
          updatedPlans.push({
            ...paymentOption,
            // Use new IDs from API response if available, otherwise keep existing
            id: planResult.plan?.id || paymentOption.id,
            squarePlanId: planResult.plan?.squarePlanId || planResult.plan?.id || paymentOption.squarePlanId,
            slug: planResult.plan?.slug || paymentOption.slug,
          });

        } catch (error) {
          console.error(`Error updating plan for ${paymentOption.displayName}:`, error);
          errors.push(`Failed to update plan "${paymentOption.displayName}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      if (errors.length > 0) {
        return {
          success: false,
          error: `Some payment options failed to update: ${errors.join('; ')}`,
          code: 'PARTIAL_FAILURE',
        };
      }

      return {
        success: true,
        data: { paymentOptions: updatedPlans },
      };

    } catch (error) {
      console.error('Error updating payment options:', error);
      return {
        success: false,
        error: 'Network error occurred while updating payment options',
        code: 'NETWORK_ERROR',
      };
    }
  }

  /**
   * Get payment options for a program
   * Uses the correct backend endpoint: /api/subscription/plan
   */
  static async getPaymentOptions(
    _programId: string,
    _programType: string
  ): Promise<{ success: boolean; data?: BasePaymentOption[]; error?: string }> {
    try {
      // Check if user appears to be authenticated
      if (!this.isAuthenticated()) {
        console.error('PaymentOptionsAPI: User not authenticated');
        return {
          success: false,
          error: 'User not authenticated. Please log in again.',
        };
      }

      const response = await fetch(
        `${this.baseURL}/api/subscription/plan`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // Note: Backend uses HTTP-only cookies for authentication
            // No Authorization header needed - cookies are sent automatically
          },
          credentials: 'include', // Include cookies for authentication
        }
      );

      const result = await response.json();

      if (!response.ok) {
        console.error('PaymentOptionsAPI: Failed to fetch payment options:', {
          status: response.status,
          statusText: response.statusText,
          response: result,
        });
        return {
          success: false,
          error: result.message || 'Failed to fetch payment options',
        };
      }

      // Transform backend plan format to frontend format
      const transformedPlans = (result.plans || []).map((plan: any) => ({
        id: plan.id || plan.slug,
        planType: this.mapBackendPlanTypeToFrontend(plan.planType),
        displayName: plan.displayName,
        numberOfPayments: plan.numberOfPayments || 1,
        amount: plan.amount,
        frequency: this.mapPlanTypeToFrequency(plan.planType),
        youPay: plan.amount * (plan.numberOfPayments || 1),
        youSave: '0%',
        paymentTags: '',
        cardTag: { available: false },
        squarePlanId: plan.squarePlanId,
        slug: plan.slug,
      }));

      return {
        success: true,
        data: transformedPlans,
      };

    } catch (error) {
      console.error('Error fetching payment options:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching payment options',
      };
    }
  }

  /**
   * Delete a payment option
   * Uses the correct backend endpoint: /api/subscription/plan/:planId
   */
  static async deletePaymentOption(
    _programId: string,
    paymentOptionId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Check if user appears to be authenticated
      if (!this.isAuthenticated()) {
        console.error('PaymentOptionsAPI: User not authenticated');
        return {
          success: false,
          error: 'User not authenticated. Please log in again.',
        };
      }

      const response = await fetch(
        `${this.baseURL}/api/subscription/plan/${paymentOptionId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            // Note: Backend uses HTTP-only cookies for authentication
            // No Authorization header needed - cookies are sent automatically
          },
          credentials: 'include', // Include cookies for authentication
        }
      );

      if (!response.ok) {
        const result = await response.json();
        console.error('PaymentOptionsAPI: Failed to delete payment option:', {
          status: response.status,
          statusText: response.statusText,
          response: result,
          planId: paymentOptionId,
        });
        return {
          success: false,
          error: result.message || 'Failed to delete payment option',
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting payment option:', error);
      return {
        success: false,
        error: 'Network error occurred while deleting payment option',
      };
    }
  }

  /**
   * Maps frontend plan type to backend plan type format
   */
  private static mapPlanTypeToBackend(planType: string): string {
    switch (planType) {
      case 'FULL_PAY':
        return 'FULL_PAY';
      case 'DAILY':
        return 'DAILY';
      case 'BI_WEEKLY':
        return 'BI_WEEKLY';
      case 'MONTHLY':
        return 'MONTHLY';
      default:
        return 'FULL_PAY';
    }
  }

  /**
   * Maps backend plan type to frontend plan type format
   */
  private static mapBackendPlanTypeToFrontend(planType: string): string {
    switch (planType) {
      case 'FULL_PAY':
        return 'FULL_PAY';
      case 'DAILY':
        return 'DAILY';
      case 'BI_WEEKLY':
        return 'BI_WEEKLY';
      case 'MONTHLY':
        return 'MONTHLY';
      default:
        return 'FULL_PAY';
    }
  }

  /**
   * Maps plan type to frequency
   */
  private static mapPlanTypeToFrequency(planType: string): string {
    switch (planType) {
      case 'FULL_PAY':
        return 'ONCE';
      case 'DAILY':
        return 'DAILY';
      case 'BI_WEEKLY':
        return 'BI_WEEKLY';
      case 'MONTHLY':
        return 'MONTHLY';
      default:
        return 'ONCE';
    }
  }
}
